import React from 'react'
import { useLocation } from 'react-router-dom'
import { AppLink as Link } from '@components/AppLink'
import { isStatusHost } from '@utils/appDomain'
import { observer } from 'mobx-react-lite'
import { LandingLogo } from './LandingLogo'
import { SectionLink } from './sectionNav'
import { useStore } from '@store/StoreProvider'
import './publicFooter.css'
import { SUPPORT_BOT_URL, TELEGRAM_NEWS_URL, supportBotLabel } from '@constants/support'

const year = new Date().getFullYear()

export const PublicFooter: React.FC = observer(() => {
  const { i18n } = useStore()
  const { pathname } = useLocation()
  const statusActive = pathname === '/status' || (isStatusHost() && pathname === '/')
  const t = (key: string, fallback: string) => {
    const value = i18n.t(key)
    return value === key ? fallback : value
  }

  const linkClass = (active: boolean) => (active ? 'f-lnk is-current' : 'f-lnk')
  const termsLabel = t('footer.terms', i18n.language === 'ru' ? 'Пользовательское соглашение' : 'Terms of Service')

  return (
    <footer className="pub-foot border-t border-white/5 mt-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="f-grid grid gap-8 pt-12 pb-7">
          <div>
            <div className="mb-3">
              <LandingLogo size="sm" />
            </div>
            <p className="f-slogan text-[13.5px] leading-relaxed text-slate-500 max-w-[34ch]">
              {t(
                'landing.mission',
                'Приватное общение с клиентами. Храним только то, что нужно для работы.',
              )}
            </p>
            <Link
              to="/status"
              className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full text-[11.5px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/15 transition"
            >
              <span className="relative w-1.5 h-1.5 rounded-full bg-current">
                <span className="f-ping absolute inset-0 rounded-full bg-current" />
              </span>
              {t('footer.status_ok', 'Все системы работают')}
            </Link>
          </div>

          <div className="f-col">
            <div className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-slate-500 mb-2 sm:mb-3">
              {t('footer.help', 'Помощь')}
            </div>
            <ul className="f-links sm:block text-sm">
              <li>
                <button type="button" data-chatx-open className="f-lnk">
                  {t('nav.help', 'Поддержка')}
                </button>
              </li>
              <li>
                <a href={SUPPORT_BOT_URL} target="_blank" rel="noopener noreferrer" className="f-lnk">
                  {supportBotLabel((k) => i18n.t(k), i18n.language)}
                </a>
              </li>
              <li>
                <a href={TELEGRAM_NEWS_URL} target="_blank" rel="noopener noreferrer" className="f-lnk">
                  {t('footer.news', 'Новости и релизы')}
                </a>
              </li>
              <li>
                <Link to="/knowledge-base" className={linkClass(pathname.startsWith('/knowledge-base'))}>
                  {t('nav.knowledge_base', 'База знаний')}
                </Link>
              </li>
            </ul>
          </div>

          <div className="f-col">
            <div className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-slate-500 mb-2 sm:mb-3">
              {t('footer.service', 'Сервис')}
            </div>
            <ul className="f-links sm:block text-sm">
              <li>
                <SectionLink section="pricing" className="f-lnk">
                  {t('footer.pricing', 'Тарифы и оплата')}
                </SectionLink>
              </li>
              <li>
                <Link to="/status" className={linkClass(statusActive)}>
                  {t('footer.service_status', 'Статус сервиса')}
                </Link>
              </li>
            </ul>
          </div>

          <div className="f-col">
            <div className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-slate-500 mb-2 sm:mb-3">
              {t('footer.legal', 'Правовое')}
            </div>
            <ul className="f-links sm:block text-sm">
              <li>
                <Link to="/terms" className={linkClass(pathname === '/terms' || pathname === '/offer')}>
                  {termsLabel}
                </Link>
              </li>
              <li>
                <Link to="/policy" className={linkClass(pathname === '/policy')}>
                  {t('landing.footer.privacy_agreement', 'Политика конфиденциальности')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 flex flex-wrap items-center gap-x-5 gap-y-2 py-5 text-xs text-slate-500">
          <span>© {year} ChatX</span>
          <span className="flex-1 min-w-[240px] leading-relaxed">
            {t(
              'landing.legal.disclaimer',
              'Запрещено использовать платформу для противоправной деятельности. Нарушение условий ведёт к блокировке без возврата средств.',
            )}
          </span>
        </div>
      </div>
    </footer>
  )
})
